import { useMemo } from "react";
import type { JSONContent } from "@tiptap/react";
import { useStudyBuilderStore } from "@/stores/study-builder-store";

/**
 * Builds a TipTap-compatible JSONContent document from the study builder store.
 */
export function useProtocolDocument(): JSONContent {
  const build = useStudyBuilderStore((s) => s.build);
  const arms = useStudyBuilderStore((s) => s.arms);
  const endpoints = useStudyBuilderStore((s) => s.endpoints);
  const eligibilityCriteria = useStudyBuilderStore(
    (s) => s.eligibilityCriteria,
  );
  const visits = useStudyBuilderStore((s) => s.visits);
  const crfs = useStudyBuilderStore((s) => s.crfs);

  return useMemo(() => {
    const content: JSONContent[] = [];

    // ── Title Page ────────────────────────────────────
    content.push(heading(1, build?.studyTitle ?? "Untitled Protocol"));
    if (build?.phase || build?.indication) {
      content.push(
        paragraph(
          [build.phase ? `Phase ${build.phase}` : null, build.indication]
            .filter(Boolean)
            .join(" — "),
        ),
      );
    }
    content.push(paragraph(""));
    content.push(rule());

    // ── 1.0 Protocol Synopsis ────────────────────────
    content.push(heading(2, "1.0 Protocol Synopsis"));
    const synopsisRows: string[][] = [];
    if (build?.protocolNumber)
      synopsisRows.push(["Protocol Number", build.protocolNumber]);
    if (build?.studyTitle)
      synopsisRows.push(["Study Title", build.studyTitle]);
    if (build?.phase) synopsisRows.push(["Phase", build.phase]);
    if (build?.indication)
      synopsisRows.push(["Indication", build.indication]);
    if (build?.sponsor) synopsisRows.push(["Sponsor", build.sponsor]);
    if (build?.estimatedSampleSize != null)
      synopsisRows.push([
        "Target Enrollment",
        String(build.estimatedSampleSize),
      ]);
    if (synopsisRows.length > 0) {
      content.push(table(["Field", "Value"], synopsisRows));
    } else {
      content.push(paragraph("No synopsis data available."));
    }

    // ── 2.0 Study Flow Diagram ───────────────────────
    content.push(heading(2, "2.0 Study Flow Diagram"));
    content.push(
      paragraph(
        "Refer to the interactive flowchart in the Study Design view.",
      ),
    );

    // ── 3.0 Study Objective ──────────────────────────
    content.push(heading(2, "3.0 Study Objective"));
    content.push(
      paragraph(
        build?.studyObjective ??
          "Study objective has not been defined yet.",
      ),
    );

    // ── 4.0 Study Design and Endpoints ───────────────
    content.push(heading(2, "4.0 Study Design and Endpoints"));
    if (endpoints.length > 0) {
      const grouped: Record<string, typeof endpoints> = {};
      for (const ep of endpoints) {
        const cat = ep.type || "Other";
        (grouped[cat] ??= []).push(ep);
      }
      let subNum = 1;
      for (const [type, eps] of Object.entries(grouped)) {
        content.push(heading(3, `4.${subNum} ${type} Endpoints`));
        for (const ep of eps) {
          content.push(
            bulletItem(
              `${ep.name}${ep.description ? ` — ${ep.description}` : ""}`,
            ),
          );
        }
        subNum++;
      }
    } else {
      content.push(paragraph("No endpoints defined."));
    }

    // ── 5.0 Patient Population and Eligibility ──────
    content.push(heading(2, "5.0 Patient Population and Eligibility"));
    const inclusion = eligibilityCriteria.filter(
      (c) => c.type === "inclusion",
    );
    const exclusion = eligibilityCriteria.filter(
      (c) => c.type === "exclusion",
    );
    if (inclusion.length > 0) {
      content.push(heading(3, "5.1 Inclusion Criteria"));
      for (const c of inclusion) {
        content.push(bulletItem(c.text));
      }
    }
    if (exclusion.length > 0) {
      content.push(heading(3, "5.2 Exclusion Criteria"));
      for (const c of exclusion) {
        content.push(bulletItem(c.text));
      }
    }
    if (inclusion.length === 0 && exclusion.length === 0) {
      content.push(paragraph("No eligibility criteria defined."));
    }

    // ── 6.0 Study Procedures ────────────────────────
    content.push(heading(2, "6.0 Study Procedures"));
    if (visits.length > 0) {
      content.push(heading(3, "6.1 Schedule of Events"));
      const visitHeaders = ["Visit", "Day/Week", "Window"];
      const visitRows = visits.map((v) => [
        v.name,
        v.studyDay != null
          ? `Day ${v.studyDay}`
          : v.studyWeek != null
            ? `Week ${v.studyWeek}`
            : "—",
        v.windowDaysBefore || v.windowDaysAfter
          ? `−${v.windowDaysBefore}/+${v.windowDaysAfter}d`
          : "—",
      ]);
      content.push(table(visitHeaders, visitRows));
    }
    if (crfs.length > 0) {
      content.push(heading(3, "6.2 Data Collection Forms"));
      const crfHeaders = ["CRF", "Category", "Sections", "Fields"];
      const crfRows = crfs.map((c) => [
        c.name,
        c.category,
        String(c.sections.length),
        String(c.sections.reduce((sum, s) => sum + s.fields.length, 0)),
      ]);
      content.push(table(crfHeaders, crfRows));
    }
    if (visits.length === 0 && crfs.length === 0) {
      content.push(paragraph("No study procedures defined."));
    }

    // ── 7.0 Treatment Arms ──────────────────────────
    content.push(heading(2, "7.0 Treatment Arms"));
    if (arms.length > 0) {
      const armHeaders = ["Arm", "Type", "Intervention", "Ratio"];
      const armRows = arms.map((a) => [
        a.name,
        a.type ?? "—",
        a.intervention ?? "—",
        a.randomizationRatio ?? "—",
      ]);
      content.push(table(armHeaders, armRows));
    } else {
      content.push(paragraph("No treatment arms defined."));
    }

    return { type: "doc", content };
  }, [build, arms, endpoints, eligibilityCriteria, visits, crfs]);
}

// ── TipTap JSON helpers ──────────────────────────────

function heading(level: 1 | 2 | 3, text: string): JSONContent {
  return {
    type: "heading",
    attrs: { level },
    content: [{ type: "text", text }],
  };
}

function paragraph(text: string): JSONContent {
  if (!text) return { type: "paragraph" };
  return {
    type: "paragraph",
    content: [{ type: "text", text }],
  };
}

function bulletItem(text: string): JSONContent {
  return {
    type: "bulletList",
    content: [
      {
        type: "listItem",
        content: [paragraph(text)],
      },
    ],
  };
}

function rule(): JSONContent {
  return { type: "horizontalRule" };
}

function table(headers: string[], rows: string[][]): JSONContent {
  const headerRow: JSONContent = {
    type: "tableRow",
    content: headers.map((h) => ({
      type: "tableHeader",
      content: [paragraph(h)],
    })),
  };

  const dataRows: JSONContent[] = rows.map((row) => ({
    type: "tableRow",
    content: row.map((cell) => ({
      type: "tableCell",
      content: [paragraph(cell)],
    })),
  }));

  return {
    type: "table",
    content: [headerRow, ...dataRows],
  };
}
